import React from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@skyslope/auth-react';

import { getRoute } from 'appRoutes';
import LocalStorageItem from 'constants/LocalStorageItem';
import { getPath, routes } from 'constants/routes';
import useGetUserSettingsQuery from 'hooks/domain/user/queries/useGetUserSettingsQuery';
import { useIsOnboarding } from 'hooks/useIsOnboarding';
import useIsPermanentEmailNeeded from 'hooks/useIsPermanentEmailNeeded';
import useLocalStorage from 'hooks/useLocalStorage';
import useWidgetQueryParams from 'hooks/useWidgetQueryParams';

/**
 * This hook returns two functions, one to redirect away from the onboarding page
 * and one to redirect to the onboarding flow.
 *
 * A previous iteration of this hook didn't return anything, and just had two effects
 * that were called in the SecurityDependentEffects component. But the disadvantage of
 * that is that you can't trigger one of the redirections without also triggering the
 * other. By returning the functions, you can call them independently; the onboarding
 * flow can be responsible for redirecting away from the onboarding page, and the
 * SecurityDependentEffects component can separately be responsible for redirecting
 * to the onboarding flow.
 *
 * These two functions could be two separate hooks, but they are closely related and
 * would have a lot of duplicate code. So it makes sense to keep them together.
 */
export function useRedirectToAndFromOnboarding() {
  const history = useHistory();
  const location = useLocation();

  const currentRoute = getRoute(location.pathname);
  const currentPath = currentRoute?.routerProps.path;
  const isNakedRoute = currentRoute?.isNaked;

  const isOnOnboardingPage = useIsOnboarding();
  const widgetParams = useWidgetQueryParams();
  const { addItem, getItem, removeItem } = useLocalStorage();
  const isPermanentEmailNeeded = useIsPermanentEmailNeeded();

  const { isAuthenticated } = useAuth().authState;
  const userSettings = useGetUserSettingsQuery();
  const isInitialized = userSettings.data?.isInitialized;

  const shouldRedirect =
    isAuthenticated && !isNakedRoute && isInitialized != null && !currentPath?.includes('/verify/');

  const redirectAwayFromOnboarding = React.useCallback(
    (args?: { overrideIsInitialized: boolean }) => {
      const finalIsInitialized = args?.overrideIsInitialized || isInitialized;

      if (finalIsInitialized && shouldRedirect && !isPermanentEmailNeeded && isOnOnboardingPage) {
        const redirectUrl = getItem(LocalStorageItem.OnboardingRedirectUrl);
        if (redirectUrl) {
          removeItem(LocalStorageItem.OnboardingRedirectUrl);
          history.push(redirectUrl);
        } else if (widgetParams.fileId) {
          history.push(getPath(routes.fileDetailsDocuments, { id: widgetParams.fileId }));
        } else {
          history.push(routes.homeTownBuffet);
        }
      }
    },
    [
      isInitialized,
      shouldRedirect,
      isOnOnboardingPage,
      history,
      widgetParams.fileId,
      getItem,
      removeItem,
      isPermanentEmailNeeded,
    ]
  );

  const redirectToOnboarding = React.useCallback(() => {
    if (!isInitialized && shouldRedirect && !isOnOnboardingPage) {
      addItem(LocalStorageItem.OnboardingRedirectUrl, `${location.pathname}${location.search}`);
      history.push({
        pathname: routes.onboardingEmail,
        search: location.search,
      });
    }
  }, [isInitialized, shouldRedirect, isOnOnboardingPage, history, addItem, location.pathname, location.search]);

  return { redirectAwayFromOnboarding, redirectToOnboarding };
}
